/**
 * Rep Scorecards — Cockpit skin
 * ─────────────────────────────
 * Same data, same selectors, same routing. Restructured for editorial
 * calm: compact masthead with hero close-rate, hairline rep list,
 * detail panel with eyebrowed sections instead of icon-heavy cards.
 */
import { useState, useMemo } from "react";
import { useAppData } from "@/data/AppDataContext";
import { computeRepScorecard, TIER_CONFIG, type RepScorecard } from "@/data/commissionEngine";
import { computeRepPerformanceInsights, type RepPerformanceInsight } from "@/lib/repPerformanceIntelligence";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  SectionEyebrow, CockpitListShell, CockpitColumns, CockpitRow,
  CockpitEmpty, toneFor,
} from "@/components/cockpit/CockpitShell";

function pct(v: number): string { return `${Math.round(v * 100)}%`; }
function currency(v: number): string { return `$${Math.round(v).toLocaleString()}`; }

export default function RepScorecardsCockpit() {
  const { appointmentResults, followUpTasks, jobs } = useAppData();
  
  const [selectedRep, setSelectedRep] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState("all");

  const reps = useMemo(
    (): string[] => [...new Set(appointmentResults.map(r => r.assignedRep))].sort(),
    [appointmentResults]
  );

  const scorecards = useMemo((): RepScorecard[] => reps.map(rep => {
    const repTasks = followUpTasks.filter(t => t.assignedTo === rep);
    const completedTasks = repTasks.filter(t => t.status === "completed");
    const followUpRate = repTasks.length > 0 ? completedTasks.length / repTasks.length : 1.0;
    return computeRepScorecard(rep, appointmentResults, followUpRate, jobs);
  }), [reps, appointmentResults, followUpTasks, jobs]);

  const perfInsights = useMemo(() => computeRepPerformanceInsights(), []);

  const filtered = useMemo(() => {
    if (tierFilter === "all") return scorecards;
    return scorecards.filter(s => s.performanceTier === tierFilter);
  }, [scorecards, tierFilter]);

  const totals = useMemo(() => ({
    revenue: scorecards.reduce((s, sc) => s + sc.totalRevenueSold, 0),
    deals: scorecards.reduce((s, sc) => s + sc.salesClosed, 0),
    avgClose: scorecards.length > 0 ? scorecards.reduce((s, sc) => s + sc.closeRate, 0) / scorecards.length : 0,
    avgTicket: scorecards.length > 0 ? scorecards.reduce((s, sc) => s + sc.averageTicket, 0) / scorecards.length : 0,
  }), [scorecards]);

  const activeScorecard = selectedRep ? scorecards.find(s => s.repName === selectedRep) : null;
  const activeInsight = selectedRep ? perfInsights.find(i => i.repName === selectedRep) : undefined;

  return (
    <CockpitPage>
      <CockpitMasthead
        eyebrow="Sales · Rep performance"
        title="Rep Scorecards"
        right={
          <>
            <HeroMetric label="Avg close" value={pct(totals.avgClose)} hero />
            <HeroDivider />
            <HeroMetric label="Deals" value={totals.deals} />
            <HeroMetric label="Avg ticket" value={currency(totals.avgTicket)} />
            <HeroMetric label="Revenue" value={`$${(totals.revenue / 1000).toFixed(totals.revenue >= 100_000 ? 0 : 1)}k`} />
          </>
        }
      />

      {/* Tier filter — single control, no card */}
      <div className="flex items-center gap-3">
        <SectionEyebrow>Tier</SectionEyebrow>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="h-9 w-44 text-sm bg-transparent border-border/60"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-sm">All tiers</SelectItem>
            <SelectItem value="elite" className="text-sm">Elite</SelectItem>
            <SelectItem value="strong" className="text-sm">Strong</SelectItem>
            <SelectItem value="solid" className="text-sm">Solid</SelectItem>
            <SelectItem value="developing" className="text-sm">Developing</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground tabular-nums">{filtered.length} reps</span>
      </div>

      {/* Two-column: rep list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_1.6fr] gap-8">
        {/* Rep list — hairline rows */}
        <div>
          {filtered.length === 0 ? (
            <CockpitEmpty message="No appointment results yet. Reps appear after submitting outcomes." />
          ) : (
            <CockpitListShell>
              <CockpitColumns template="2fr 1fr 1fr">
                <span>Rep</span>
                <span className="text-right">Close</span>
                <span className="text-right">Sold</span>
              </CockpitColumns>
              {filtered.map(sc => {
                const tier = TIER_CONFIG[sc.performanceTier];
                const insight = perfInsights.find(i => i.repName === sc.repName);
                const isActive = selectedRep === sc.repName;
                return (
                  <button
                    key={sc.repName}
                    onClick={() => setSelectedRep(sc.repName)}
                    className={cn(
                      "block w-full text-left px-1 py-3 transition-colors",
                      isActive ? "bg-muted/40" : "hover:bg-muted/30"
                    )}
                  >
                    <div className="grid grid-cols-[2fr_1fr_1fr] gap-6 items-center">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{sc.repName}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          <span className={cn("font-medium", tier.color)}>{tier.label}</span>
                          {insight && <> · {insight.highFitConversion}% HF · {insight.region}</>}
                        </p>
                      </div>
                      <span className={cn(
                        "text-sm tabular-nums font-medium text-right",
                        sc.closeRate >= 0.30 ? "text-success" :
                        sc.closeRate >= 0.15 ? "text-warning" : "text-destructive"
                      )}>{pct(sc.closeRate)}</span>
                      <span className="text-sm tabular-nums font-medium text-foreground text-right">{sc.salesClosed}</span>
                    </div>
                  </button>
                );
              })}
            </CockpitListShell>
          )}
        </div>

        {/* Detail */}
        <div>
          {activeScorecard
            ? <ScorecardDetail scorecard={activeScorecard} perfInsight={activeInsight} />
            : <CockpitEmpty message="Select a rep to view their scorecard." />}
        </div>
      </div>
    </CockpitPage>
  );
}

function ScorecardDetail({ scorecard, perfInsight }: { scorecard: RepScorecard; perfInsight?: RepPerformanceInsight }) {
  const tier = TIER_CONFIG[scorecard.performanceTier];
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between gap-6 pb-5 border-b border-border/60">
        <div>
          <p className={cn("text-[10px] uppercase tracking-[0.18em] font-medium", tier.color)}>{tier.label} performer</p>
          <h2 className="text-xl font-semibold tracking-tight text-foreground mt-1">{scorecard.repName}</h2>
          {perfInsight && <p className="text-xs text-muted-foreground mt-1">{perfInsight.region}</p>}
        </div>
        <HeroMetric label="Close rate" value={pct(scorecard.closeRate)} hero />
      </div>

      {/* Opportunity intelligence */}
      {perfInsight && (
        <section className="space-y-3">
          <SectionEyebrow>Opportunity intelligence</SectionEyebrow>
          <div className="grid grid-cols-3 gap-6 border-y border-border/60 py-4">
            <Stat label="Net conversion" value={`${perfInsight.netConversion}%`} sub={`${perfInsight.totalLeadsAssigned} leads`} />
            <Stat
              label="High-fit conversion"
              value={`${perfInsight.highFitConversion}%`}
              sub={`${perfInsight.highFitLeads} HF leads`}
              tone={toneFor(perfInsight.highFitConversion, 50, 35)}
            />
            <Stat label="Revenue / lead" value={`$${perfInsight.revenuePerLead.toLocaleString()}`} sub={`Avg quality: ${perfInsight.avgLeadQuality}`} />
          </div>
          <p className={cn(
            "text-sm leading-snug",
            perfInsight.insightSeverity === "positive" ? "text-success" :
            perfInsight.insightSeverity === "caution" ? "text-warning" : "text-muted-foreground"
          )}>{perfInsight.insight}</p>
        </section>
      )}

      {/* Performance */}
      <section className="space-y-3">
        <SectionEyebrow>Performance</SectionEyebrow>
        <div className="grid grid-cols-3 gap-6 border-y border-border/60 py-4">
          <Stat label="Appts ran" value={scorecard.appointmentsRan} />
          <Stat label="Demos" value={scorecard.demosCompleted} />
          <Stat label="No demos" value={scorecard.noDemos} />
          <Stat label="Demo → sale" value={pct(scorecard.demoToSaleRate)} />
          <Stat label="Appt → sale" value={pct(scorecard.appointmentToSaleRate)} />
          <Stat label="Sales closed" value={scorecard.salesClosed} />
        </div>
      </section>

      {/* Financial */}
      <section className="space-y-3">
        <SectionEyebrow>Financial</SectionEyebrow>
        <div className="grid grid-cols-3 gap-6 border-y border-border/60 py-4">
          <Stat label="Revenue sold" value={currency(scorecard.totalRevenueSold)} />
          <Stat label="Average ticket" value={currency(scorecard.averageTicket)} />
          <Stat label="Financing mix" value={pct(scorecard.financingMix)} sub={`${scorecard.financingCount} fin / ${scorecard.cashCount} cash`} />
        </div>
      </section>

      {/* Discipline */}
      <section className="space-y-3">
        <SectionEyebrow>Discipline</SectionEyebrow>
        <div className="border-y border-border/60 divide-y divide-border/60">
          {[
            { label: "Result submission rate", value: scorecard.resultSubmissionRate },
            { label: "Follow-up completion", value: scorecard.followUpCompletionRate },
            { label: "Reschedule recovery", value: scorecard.rescheduleRecoveryRate },
          ].map(m => (
            <div key={m.label} className="flex items-center gap-4 py-3">
              <p className="text-sm text-foreground/90 flex-1">{m.label}</p>
              <div className="flex-1 h-px bg-border/60 relative overflow-hidden">
                <div
                  className={cn(
                    "absolute inset-y-0 left-0",
                    m.value >= 0.9 ? "bg-success" : m.value >= 0.7 ? "bg-warning" : "bg-destructive"
                  )}
                  style={{ width: `${m.value * 100}%`, height: "2px", top: "-0.5px" }}
                />
              </div>
              <span className={cn(
                "text-sm tabular-nums font-medium w-12 text-right",
                m.value >= 0.9 ? "text-success" : m.value >= 0.7 ? "text-warning" : "text-destructive"
              )}>{pct(m.value)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string | number; sub?: string; tone?: "good" | "warn" | "bad" }) {
  const toneCls =
    tone === "good" ? "text-success" :
    tone === "warn" ? "text-warning" :
    tone === "bad" ? "text-destructive" : "text-foreground";
  return (
    <div>
      <p className={cn("text-2xl font-semibold tabular-nums tracking-tight leading-none", toneCls)}>{value}</p>
      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mt-2">{label}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
